import React, { useEffect, useRef, useState } from 'react';
import { buildAttentionModel, heatColor, classifyWeight, summarizePlacements, layoutPins, ZONES } from '../gaze/gazeModel';

const norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Attention-heatmap overlay for HTML-rendered resumes (generated-resume
 * templates and mammoth-converted DOCX previews), content-aware v2.
 *
 * Landmarks come from the real DOM: block elements' positions, font sizes and
 * text feed the same attention model the PDF overlay uses, so the name block,
 * section tops, the most recent role and dates get genuine fixation bumps.
 * Keywords are located precisely with DOM Ranges and marked with numbered,
 * collision-free pins matching the summary chips.
 */
export default function HtmlGazeOverlay({ enabled, keywords = [], onStats, children }) {
  const hostRef = useRef(null);
  const heatRef = useRef(null);
  const [view, setView] = useState(null); // { size, model, pins }

  useEffect(() => {
    if (!enabled) { setView(null); return; }
    const host = hostRef.current;
    if (!host) return;

    const measure = () => {
      const hostRect = host.getBoundingClientRect();
      const totalH = host.scrollHeight || hostRect.height;
      const totalW = host.scrollWidth || hostRect.width;

      // Pseudo-tokens from block-level elements: position + font size + text.
      // Good enough for the model's landmark detection (name, sections, dates).
      const tokens = [];
      const blocks = host.querySelectorAll('h1,h2,h3,h4,p,li,div,span,td');
      for (const el of blocks) {
        if (el.children.length > 0 && !el.matches('li')) continue; // leaf-ish only
        const text = norm(el.textContent);
        if (!text) continue;
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) continue;
        const fs = parseFloat(window.getComputedStyle(el).fontSize) || 12;
        tokens.push({
          str: el.textContent,
          x: r.left - hostRect.left + host.scrollLeft,
          y: r.top - hostRect.top + host.scrollTop,
          w: r.width,
          h: fs,
        });
      }

      const model = buildAttentionModel({ width: totalW, height: totalH, pageIndex: 0, tokens });

      // Precise keyword rects via DOM Ranges; weight from the model.
      const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (norm(n.textContent)) textNodes.push(n);
      }

      const placements = keywords.map(keyword => {
        const kw = norm(keyword);
        if (!kw) return { keyword, found: false };
        let best = null;
        for (const node of textNodes) {
          const text = node.textContent.toLowerCase();
          const idx = text.indexOf(kw);
          if (idx === -1) continue;
          try {
            const range = document.createRange();
            range.setStart(node, idx);
            range.setEnd(node, Math.min(idx + keyword.length, node.textContent.length));
            const r = range.getBoundingClientRect();
            if (!r.width && !r.height) continue;
            const x = r.left - hostRect.left + host.scrollLeft;
            const y = r.top - hostRect.top + host.scrollTop;
            const weight = model.weightAt(x + r.width / 2, y + r.height / 2);
            if (!best || weight > best.weight) {
              best = { keyword, found: true, x, y, w: r.width, h: r.height, weight, zone: classifyWeight(weight) };
            }
          } catch {
            /* detached node, skip */
          }
        }
        return best || { keyword, found: false };
      });

      const pins = layoutPins(placements);
      setView({ size: { w: totalW, h: totalH }, model, pins });
      if (onStats) {
        onStats({
          ...summarizePlacements(placements),
          pins: pins.map(p => ({ keyword: p.keyword, index: p.index })),
        });
      }
    };

    measure();
    // Content changes (template switch, chat edit) resize the host, so the
    // observer re-measures; depending on `children` directly would re-run on
    // every parent render and loop onStats -> parent setState -> render.
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [enabled, keywords, onStats]);

  // Paint the combined attention field.
  useEffect(() => {
    if (!enabled || !view) return;
    const canvas = heatRef.current;
    if (!canvas || !view.size.w || !view.size.h) return;
    const CELL = 14;
    canvas.width = Math.ceil(view.size.w / CELL);
    canvas.height = Math.ceil(view.size.h / CELL);
    const ctx = canvas.getContext('2d');
    for (let gy = 0; gy < canvas.height; gy++) {
      for (let gx = 0; gx < canvas.width; gx++) {
        const w = view.model.weightAt((gx + 0.5) * CELL, (gy + 0.5) * CELL);
        ctx.fillStyle = heatColor(w);
        ctx.fillRect(gx, gy, 1, 1);
      }
    }
  }, [enabled, view]);

  const zoneColor = zone => (ZONES[zone] ? ZONES[zone].color : '#2563eb');

  return (
    <div ref={hostRef} className="relative">
      {children}
      {enabled && view && view.size.h > 0 && (
        <div className="absolute inset-0 pointer-events-none" style={{ width: view.size.w, height: view.size.h }}>
          <canvas
            ref={heatRef}
            className="absolute inset-0"
            style={{ width: '100%', height: '100%', filter: 'blur(12px)', opacity: 0.8 }}
          />
          {view.pins.map(p => (
            <div key={`${p.keyword}-${p.index}`}>
              <div
                className="absolute rounded-sm"
                style={{
                  left: p.x - 1,
                  top: p.y + p.h,
                  width: p.w + 2,
                  height: 2.5,
                  background: zoneColor(p.zone),
                }}
              />
              <div
                className="absolute flex items-center justify-center rounded-full text-[10px] font-bold text-white shadow"
                title={p.keyword}
                style={{
                  left: p.pinX - 9,
                  top: p.pinY - 9,
                  width: 18,
                  height: 18,
                  background: zoneColor(p.zone),
                  border: '1.5px solid #fff',
                }}
              >
                {p.index}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
